import type { Faq } from 'libs/http/api/content/content.types';

import type { EditorialStep } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection.types';

type Translate = (key: string) => string;

export const getMaActivityWhySteps = (t: Translate): EditorialStep[] => [
  {
    num: '01',
    title: t('See every company, not just the funded ones'),
    description: t(
      '600M+ companies from 400+ government registries. Deal databases index the companies that raised. Registries hold everyone.',
    ),
  },
  {
    num: '02',
    title: t('Find growth in the numbers they file'),
    description: t(
      'Screen on revenue, EBITDA, profitability, headcount and more — and filter on year-on-year growth to surface companies compounding fastest.',
    ),
  },
  {
    num: '03',
    title: t('Reach early-stage companies first'),
    description: t(
      'New incorporations land daily. Track a company from its first filing, long before it appears in a deal database.',
    ),
  },
  {
    num: '04',
    title: t('Work from the original filing'),
    description: t('Financials updated as accounts are filed, with the source document attached to every statement.'),
  },
  {
    num: '05',
    title: t('Reach the decision-maker from LinkedIn'),
    description: t(
      'Our browser extension sits on the executive’s LinkedIn profile and returns their business email and direct phone — waterfall enrichment across 10+ data partners.',
    ),
  },
];

export const getMaActivityFaq = (t: Translate): Faq[] => [
  {
    id: 'faq-ma-what',
    question: t('What is M&A activity data?'),
    answer: t(
      'M&A activity data covers the transaction history of a company: acquisitions, mergers, divestitures, funding rounds, investors, and exits. Global Database maps these events to the company profile alongside filed financials, ownership, and key people, so the full deal picture sits in one record.',
    ),
  },
  {
    id: 'faq-ma-source',
    question: t('Where does Global Database source its M&A data?'),
    answer: t(
      'Company records are sourced from more than 400 government registries across 200+ countries, combined with verified public sources for deal announcements. Every attribute carries its source, a link to the underlying filing where available, and a retrieval timestamp.',
    ),
  },
  {
    id: 'faq-ma-vs-pitchbook',
    question: t('How is Global Database different from PitchBook or Crunchbase?'),
    answer: t(
      'Deal databases focus on companies that raised funding. Global Database covers 600M+ registered companies, whether they ever took investment or not, and provides original filed financial statements from government registries rather than estimates, together with flexible licensing for redistribution.',
    ),
  },
  {
    id: 'faq-ma-private',
    question: t('Does Global Database cover private company M&A activity?'),
    answer: t(
      'Yes. Because ownership changes and mergers are recorded at the company registry, private transactions appear in the data even when they were never announced in the press.',
    ),
  },
  {
    id: 'faq-ma-funding',
    question: t('Can I track funding rounds and investors?'),
    answer: t(
      'Yes. Each company profile includes funding rounds with dates, amounts, valuations, and lead investors, plus a classified investor list covering VC firms, PE funds, corporate investors, sovereign wealth funds, family offices, and angels.',
    ),
  },
  {
    id: 'faq-ma-current',
    question: t('How current is the data?'),
    answer: t(
      'Company records are synced as each registry publishes — daily in most major jurisdictions. Newly incorporated companies are added every day, and ownership or status changes flow through as they are filed at the registry.',
    ),
  },
  {
    id: 'faq-ma-api',
    question: t('Is there an API for M&A and funding data?'),
    answer: t(
      'Yes. The REST API returns company deal history, funding rounds, and ownership data as JSON, and the Global Database MCP server makes the same data available to AI assistants, agents, and internal models. Bulk delivery as CSV, JSON, or Parquet is also available.',
    ),
  },
  {
    id: 'faq-ma-license',
    question: t('Can I license the data for resale or AI training?'),
    answer: t(
      'Yes. Global Database offers flexible commercial licensing, including reseller rights, redistribution inside your own product, and licensing for AI model training. Terms are agreed per use case.',
    ),
  },
  {
    id: 'faq-ma-countries',
    question: t('Which countries does Global Database cover?'),
    answer: t(
      'Coverage spans 200+ countries and territories through 400+ government registries, including Companies House in the UK, the German Handelsregister, French RCS, US state registries, and equivalents across Europe, the Americas, Asia, Africa, and Oceania.',
    ),
  },
  {
    id: 'faq-ma-access',
    question: t('How do I get access to the M&A activity data?'),
    answer: t(
      'Book a demo to see coverage for your target markets. Enterprise plans include platform access, the REST API and MCP server, bulk data delivery, and dedicated support.',
    ),
  },
];
